import { type FC, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import moment, { type Moment } from "moment-timezone";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { DialogHeader, ErrorMessage } from "@/shared/components";
import { useAbility } from "@/partner/shared/lib";
import { useUpdateProductPurchaseMutation } from "@/partner/entities/purchases";

interface EditPurchaseDatesButtonProps {
  id: string;
  startAt: string;
  expiredAt: string;
}

/**
 * Правка сроков покупки партнёром (референс админской EditPurchaseButton):
 * два пикера дат начала и окончания доступа. Собственный диалог, а не
 * общий ConfirmDialog кабинета: тому негде заблокировать кнопку OK по
 * валидности дат (у партнёрского ConfirmDialog нет prop `disabled`, в
 * отличие от админского), а невалидная дата пикера отдаёт невалидный
 * moment, без гейта сабмит ушёл бы пустым ISO и отрапортовал успех.
 */
export const EditPurchaseDatesButton: FC<EditPurchaseDatesButtonProps> = ({
  id,
  startAt,
  expiredAt,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const ability = useAbility();
  const [open, setOpen] = useState(false);
  const [startAtValue, setStartAtValue] = useState<Moment | null>(
    moment(startAt)
  );
  const [expiredAtValue, setExpiredAtValue] = useState<Moment | null>(
    moment(expiredAt)
  );

  const [updateProductPurchase, { loading, error, reset }] =
    useUpdateProductPurchaseMutation({
      refetchQueries: ["PurchasesList"],
      onCompleted: () => {
        enqueueSnackbar(t("purchases.actions.editSuccessSnackbar"), {
          variant: "success",
        });
        setOpen(false);
      },
      onError: () => {
        // ошибка уже отображается через ErrorMessage ниже
      },
    });

  if (!ability.can("update", "ProductPurchase")) return null;

  const datesValid = Boolean(
    startAtValue?.isValid() && expiredAtValue?.isValid()
  );

  const handleOpen = () => {
    setStartAtValue(moment(startAt));
    setExpiredAtValue(moment(expiredAt));
    reset();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const handleSubmit = () => {
    if (!startAtValue || !expiredAtValue || !datesValid) return;

    void updateProductPurchase({
      variables: {
        id,
        input: {
          startAt: startAtValue.toISOString(),
          expiredAt: expiredAtValue.toISOString(),
        },
      },
    });
  };

  return (
    <>
      <Tooltip title={t("purchases.actions.editButton")}>
        <span>
          <IconButton
            size="small"
            disabled={loading}
            onClick={(event) => {
              event.stopPropagation();
              handleOpen();
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogHeader
          title={t("purchases.actions.editDialogTitle")}
          onClose={handleClose}
          loading={loading}
        />
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <ErrorMessage error={error} />}
            <DialogContentText>
              {t("purchases.actions.editDialogText")}
            </DialogContentText>
            <DateTimePicker
              label={t("purchases.actions.startAtLabel")}
              value={startAtValue}
              ampm={false}
              format="DD MMMM YYYY HH:mm"
              onChange={setStartAtValue}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DateTimePicker
              label={t("purchases.actions.expiredAtLabel")}
              value={expiredAtValue}
              ampm={false}
              format="DD MMMM YYYY HH:mm"
              onChange={setExpiredAtValue}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            {t("purchases.actions.editCancelButton")}
          </Button>
          <Button
            variant="contained"
            disabled={!datesValid || loading}
            onClick={handleSubmit}
          >
            {t("common.actions.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
